/**
 * Interview Templates Configuration
 * Pre-configured interview scenarios with predefined questions
 */

export interface InterviewTemplate {
  id: string;
  name: string;
  description: string;
  category: 'company' | 'role';
  questions: {
    [key: string]: string[]; // interview_type -> array of questions
  };
  icon: string;
}

export const INTERVIEW_TEMPLATES: InterviewTemplate[] = [
  // FAANG Company Templates (consolidated to top 3)
  {
    id: 'faang',
    name: 'FAANG / Big Tech',
    description: 'Interview questions typical of Google, Amazon, Meta, Apple, Netflix',
    category: 'company',
    icon: '🏢',
    questions: {
      system_design: [
        'Design a distributed search engine that handles billions of queries per day',
        'Design a real-time collaboration feature (like Google Docs)',
        'Design a video streaming platform with global distribution',
        'Design a recommendation system for content or products',
        'Design a social media feed algorithm'
      ],
      coding: [
        'Find the longest substring without repeating characters',
        'Implement a graph traversal algorithm for social networks',
        'Design an efficient LRU cache',
        'Implement a distributed counter system',
        'Build a rate limiter'
      ],
      technical: [
        'How do you handle consistency at global scale?',
        'Explain the trade-offs between consistency and availability',
        'How would you design for billions of concurrent users?',
        'What are the challenges of real-time systems at scale?',
        'How do you approach performance optimization?'
      ],
      architecture_review: [
        'Review this architecture for massive scale requirements',
        'How would you handle global distribution in this system?',
        'Review this design for fault tolerance and reliability',
        'How would you optimize this for cost at scale?',
        'Review the data consistency patterns in this design'
      ],
      behavioural: [
        'Tell me about a time you worked on a project with massive scale',
        'Describe a situation where you had to optimize for performance',
        'Tell me about a time you worked on a system with billions of users',
        'Describe your approach to handling ambiguous requirements',
        'Tell me about a challenging technical problem you solved'
      ]
    }
  },
  {
    id: 'startup',
    name: 'Startup',
    description: 'Questions for startup interviews (early to growth stage)',
    category: 'company',
    icon: '🚀',
    questions: {
      system_design: [
        'Design an MVP that can quickly validate a business idea',
        'Design a system that needs to scale from 10k to 10M users',
        'Design for rapid iteration and feature changes',
        'Design a system that balances cost with growth needs',
        'Design for quick time-to-market with limited resources'
      ],
      coding: [
        'Implement a feature that can be shipped in one week',
        'Build a system to handle traffic spikes',
        'Implement efficient data processing for growth metrics',
        'Create a feature flag system for rapid iteration',
        'Build a prototype that demonstrates core value'
      ],
      technical: [
        'How do you balance technical debt with shipping speed?',
        'What\'s your approach to scaling systems that weren\'t designed for it?',
        'How do you decide between building vs using existing solutions?',
        'How do you balance speed with code quality?',
        'How do you prioritize technical work during rapid growth?'
      ],
      architecture_review: [
        'Review this architecture for startup velocity',
        'How would you make this simpler and faster to build?',
        'Review this design for cost-effectiveness',
        'How would you handle technical debt in this system?',
        'How would you make this easier to change later?'
      ],
      behavioural: [
        'Tell me about a time you shipped something quickly with limited resources',
        'Describe a situation where you had to make quick technical decisions',
        'Tell me about a time you scaled a system that wasn\'t ready',
        'Describe your approach to validating ideas quickly',
        'Tell me about a time you had to pivot technically'
      ]
    }
  },
  // Role-based Templates (consolidated to top 3)
  {
    id: 'backend',
    name: 'Backend Engineer',
    description: 'Backend-focused technical questions',
    category: 'role',
    icon: '⚙️',
    questions: {
      system_design: [
        'Design a distributed session management system',
        'Design a message queue system',
        'Design a database sharding strategy',
        'Design an API gateway with rate limiting',
        'Design a distributed cache layer'
      ],
      coding: [
        'Implement a distributed lock',
        'Build a concurrent task executor',
        'Implement a circuit breaker pattern',
        'Create a connection pool manager',
        'Implement a batch processor with retry logic'
      ],
      technical: [
        'Explain the difference between synchronous and asynchronous processing',
        'How do you handle race conditions in distributed systems?',
        'What are the trade-offs between consistency and availability?',
        'Explain database indexing and query optimization',
        'How do you handle backpressure in a streaming system?'
      ],
      architecture_review: [
        'Review this backend architecture for scalability',
        'How would you optimize database queries in this system?',
        'Review the API design and suggest improvements',
        'How would you handle data consistency in this distributed system?',
        'Review the error handling and resilience patterns'
      ],
      behavioural: [
        'Tell me about a time you optimized a slow API endpoint',
        'Describe how you handle production incidents',
        'Tell me about a time you designed a system that scaled successfully',
        'Describe your approach to database migrations',
        'Tell me about a challenging debugging experience'
      ]
    }
  },
  {
    id: 'frontend',
    name: 'Frontend Engineer',
    description: 'Frontend-focused technical questions',
    category: 'role',
    icon: '🎨',
    questions: {
      system_design: [
        'Design a client-side caching strategy for a web application',
        'Design a real-time collaboration feature (like Google Docs)',
        'Design a component library architecture',
        'Design a state management solution for a large React app',
        'Design a progressive web app architecture'
      ],
      coding: [
        'Implement a virtual scrolling list component',
        'Build a debounce/throttle utility',
        'Implement a reactive state management system',
        'Create a drag-and-drop interface',
        'Build a form validation library'
      ],
      technical: [
        'Explain the React rendering lifecycle and optimization techniques',
        'How do you handle performance issues in a large web application?',
        'Explain the difference between client-side and server-side rendering',
        'How do you manage state in a complex frontend application?',
        'Explain web accessibility (a11y) best practices'
      ],
      architecture_review: [
        'Review this frontend architecture for maintainability',
        'How would you optimize bundle size in this application?',
        'Review the component structure and suggest improvements',
        'How would you improve the performance of this frontend?',
        'Review the state management approach and data flow'
      ],
      behavioural: [
        'Tell me about a time you improved frontend performance significantly',
        'Describe how you handle browser compatibility issues',
        'Tell me about a time you had to refactor a large frontend codebase',
        'Describe your approach to responsive design',
        'Tell me about a challenging UI/UX problem you solved'
      ]
    }
  },
  {
    id: 'fullstack',
    name: 'Full-Stack Engineer',
    description: 'Full-stack engineering questions covering frontend and backend',
    category: 'role',
    icon: '🔗',
    questions: {
      system_design: [
        'Design an end-to-end authentication system',
        'Design a real-time chat application',
        'Design a file upload and processing system',
        'Design a full-stack e-commerce system',
        'Design an API with frontend integration'
      ],
      coding: [
        'Implement an end-to-end feature with frontend and backend',
        'Build an API with proper error handling and validation',
        'Implement real-time updates between frontend and backend',
        'Create a full-stack form submission flow',
        'Build an authenticated API endpoint with frontend integration'
      ],
      technical: [
        'How do you ensure consistency between frontend and backend?',
        'Explain your approach to API design',
        'How do you handle authentication and authorization across the stack?',
        'What\'s your approach to testing full-stack features?',
        'How do you handle errors across the frontend-backend boundary?'
      ],
      architecture_review: [
        'Review this full-stack architecture for maintainability',
        'How would you improve the API contract between frontend and backend?',
        'Review the data flow across the entire application',
        'How would you optimize the end-to-end performance?',
        'Review the security considerations across the stack'
      ],
      behavioural: [
        'Tell me about a time you worked on both frontend and backend of a feature',
        'Describe how you coordinate with other developers on full-stack features',
        'Tell me about a time you had to debug an issue across the stack',
        'Describe your approach to full-stack development',
        'Tell me about a challenging integration problem you solved'
      ]
    }
  },
  {
    id: 'data-ml',
    name: 'Data / ML Engineer',
    description: 'Data engineering and machine learning questions',
    category: 'role',
    icon: '📊',
    questions: {
      system_design: [
        'Design a data pipeline for processing terabytes of daily data',
        'Design a real-time feature store for ML models',
        'Design an ML model serving infrastructure',
        'Design a recommendation system architecture',
        'Design a data warehouse for analytics'
      ],
      coding: [
        'Implement a streaming data aggregation algorithm',
        'Build an efficient data processing pipeline',
        'Implement a feature extraction system',
        'Create an efficient data sampling algorithm',
        'Implement a model evaluation framework'
      ],
      technical: [
        'How do you handle data quality issues in production?',
        'Explain the trade-offs between batch and streaming processing',
        'How do you monitor ML model performance in production?',
        'What are the challenges of feature engineering at scale?',
        'How do you handle model versioning and rollback?'
      ],
      architecture_review: [
        'Review this data pipeline architecture for reliability',
        'How would you optimize this for processing efficiency?',
        'Review this ML serving architecture for latency',
        'How would you handle data drift in this system?',
        'Review this architecture for data governance compliance'
      ],
      behavioural: [
        'Tell me about a time you improved a data pipeline significantly',
        'Describe a situation where you had to handle data quality issues',
        'Tell me about a time you deployed an ML model to production',
        'Describe your approach to working with cross-functional teams',
        'Tell me about a challenging data engineering problem you solved'
      ]
    }
  }
];

/**
 * Get questions for a specific template and interview type
 */
export function getTemplateQuestions(templateId: string, interviewType: string): string[] {
  const template = INTERVIEW_TEMPLATES.find(t => t.id === templateId);
  if (!template) {
    return [];
  }
  return template.questions[interviewType] || [];
}

/**
 * Get template by ID
 */
export function getTemplate(templateId: string): InterviewTemplate | undefined {
  return INTERVIEW_TEMPLATES.find(t => t.id === templateId);
}
